import { useState, useEffect, useMemo, useRef, useCallback, lazy, Suspense } from "react";
import { describeWriteFailure } from '../utils/writeFailure';
import projectManager from "../models/projects";
import { api, getActiveWorkspaceId } from "../api/client";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import { formatTaskClock, isTaskOverdue, taskInstant, taskAssigneeIds, isBufferDone } from '../utils/taskTime';   // fix(task-dates); Block I: the one overdue rule
import { messageFromError } from '../utils/apiError';
import { showErrorToast, showToast } from '../utils/toast';
import {
  Banner, Button, ChipGroup, DashboardCard, ErrorState, Input, MoveToSheet, PageHeader, PageLayout,
  ProgressBar, SearchInput, SegmentedControl, Select, Skeleton, StatTile, StatusPill, TabGroup, TabPanel, useDelayedDelete, AddTaskButton,
} from '../ui';
import { BoardView, ListView, Pager, ProjectGroups, PersonAvatar, dueText, dropDestination } from './Team/TaskViews';
import { useTaskPage } from './Team/useTaskPage';
import { TaskDrawer, ReassignSheet } from './Team/TaskDrawer';
import { MemberDetail } from './Team/MemberDetail';
import { roleLabel } from './Team/teamPeople';
import MovingMostCard from './Team/MovingMostCard';   // 2026-10-21: whose due dates move most
import './Team/team.css';
const ProjectTracker = lazy(() => import("./ProjectTracker"));
const TeamManagementLazy = lazy(() => import("./TeamManagement"));

// ─── Helpers ──────────────────────────────────────────────────────────────────
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// Whose task it is: its assignees when it has any, otherwise its creator (utils/taskTime, the one
// rule; Carla, 2026-10-11). It was the creator AND every assignee, so a late task the owner handed
// to someone read as late for both of them, and an assignee written as { userId } was never found.
function taskPeopleIds(task) {
  const assignees = taskAssigneeIds(task);
  return new Set(assignees.length ? assignees : [task.userId].filter(Boolean).map(String));
}

// Block I: 'done' | 'overdue' | 'todo', judged by the app's one overdue rule (utils/taskTime) in
// the owner's stored timezone. `new Date('2026-10-11') < now` read a task due TODAY as overdue
// from midnight UTC; a date-only due date is due by the end of that day.
// A calendar buffer whose end has passed counts as done (Carla's item 5, 2026-10-12; utils/taskTime).
function taskStatus(task, tz) {
  if (task.completed || isBufferDone(task, tz)) return 'done';
  return isTaskOverdue(task, tz) ? 'overdue' : 'todo';
}

// Fresh-eyes 12 (Carla, 2026-10-12): each person's numbers are the SERVER'S (the team list's
// `summary`, Aidey Backend/lib/teamTaskPage.js teamTaskSummary), the same ones the Dashboard's Team
// overview tile shows. They were counted here from the rows held, and the tile counted its own.
const NO_TASKS = { total: 0, completed: 0, outstanding: 0, overdue: 0 };
function memberStatsFrom(summary, users) {
  return users.map(u => ({ user: u, ...((summary && summary.byPerson && summary.byPerson[u.id]) || NO_TASKS) }));
}

function breakdown(list, tz) {
  return {
    completed:   list.filter(t => t.completed).length,
    outstanding: list.filter(t => !t.completed).length,
    overdue:     list.filter(t => isTaskOverdue(t, tz)).length,
    total:       list.length,
  };
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
// ownOnly: the server sent the caller's own tasks (scope 'own'), so nothing here may be called the
// team's: no team totals, no per-person workload (it would credit everyone else with nothing).
function OverviewTab({ stats, summary, tasks, tz, ownOnly, onFilter, onOpenTask, onViewTasks }) {
  const [selectedMember, setSelectedMember] = useState(null);

  // Week range (Sun–Sat of current week) and month range, as before.
  const weekStart = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - d.getDay()); d.setHours(0,0,0,0); return d;
  }, []);
  const weekEnd = useMemo(() => {
    const d = new Date(weekStart); d.setDate(d.getDate() + 6); d.setHours(23,59,59,999); return d;
  }, [weekStart]);
  const monthStart = useMemo(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth(), 1); }, []);
  const monthEnd   = useMemo(() => { const n = new Date(); return new Date(n.getFullYear(), n.getMonth() + 1, 0, 23, 59, 59, 999); }, []);

  const totals = summary || null;   // the server's (fresh-eyes 12); null until it has answered
  const workload = useMemo(() => [...stats].sort((a, b) => b.outstanding - a.outstanding || a.user.name.localeCompare(b.user.name)), [stats]);
  const overdueList = useMemo(() => tasks
    .filter(t => isTaskOverdue(t, tz))
    .sort((a, b) => (taskInstant(a, tz)?.getTime() || 0) - (taskInstant(b, tz)?.getTime() || 0)), [tasks, tz]);

  // `all` is the person's row from the server's summary (fresh-eyes 12); the week and month figures
  // are worked out from the rows held, as the server does not answer them.
  function memberDetail(all) {
    const { user } = all;
    const userTasks = tasks.filter(t => taskPeopleIds(t).has(user.id));
    const inRange = (start, end) => userTasks.filter(t => {
      const d = taskInstant(t, tz);   // the task's own day, read the way overdue is (no UTC-midnight shift)
      return !!d && d >= start && d <= end;
    });
    const teamAverage = stats.length > 0 && totals ? totals.outstanding / stats.length : 0;
    const ratio = teamAverage > 0 ? all.outstanding / teamAverage : (all.outstanding > 0 ? 2 : 0);
    return {
      user,
      pct: all.total > 0 ? Math.round(all.completed / all.total * 100) : 0,
      week: breakdown(inRange(weekStart, weekEnd), tz),
      month: breakdown(inRange(monthStart, monthEnd), tz),
      outstanding: all.outstanding,
      teamAverage,
      load: ratio > 1.4 ? 'Heavy' : ratio > 0.6 ? 'Normal' : 'Light',
    };
  }

  const kpis = [
    { label: ownOnly ? 'Your tasks' : 'Total tasks', value: totals ? totals.total : '—', color: 'pastel-periwinkle', filter: 'all' },
    { label: 'Completed', value: totals ? totals.completed : '—', color: 'pastel-green', filter: 'completed' },
    { label: 'Outstanding', value: totals ? totals.outstanding : '—', color: 'pastel-yellow', filter: 'outstanding' },
    { label: 'Overdue', value: totals ? totals.overdue : '—', color: 'pastel-coral', filter: 'overdue' },
  ];

  return (
    <div className="td-stack">
      <div className="td-kpis">
        {kpis.map(k => (
          <button key={k.label} type="button" className="td-kpi" onClick={() => onFilter(k.filter)}
            aria-label={`${k.label}: ${k.value}. Show ${k.filter === 'all' ? 'all of them' : 'them'} on the task board`}>
            <StatTile card color={k.color} label={k.label} value={k.value} />
          </button>
        ))}
      </div>

      <div className="td-cards">
        {!ownOnly && (
          <DashboardCard title="Team overview" tint="pastel-lilac" meta={<StatusPill tone="neutral">{plural(stats.length, 'person', 'people')}</StatusPill>}>
            {workload.length === 0 ? (
              <p className="td-muted">No one is on the team yet.</p>
            ) : (
              <ul className="td-people">
                {workload.map(s => {
                  const pct = s.total > 0 ? Math.round(s.completed / s.total * 100) : 0;
                  return (
                    <li key={s.user.id}>
                      <button type="button" className="td-person" onClick={() => setSelectedMember(memberDetail(s))}>
                        <PersonAvatar user={s.user} size={36} />
                        <span className="td-person__text">
                          <span className="td-person__name">{s.user.name}</span>
                          <span className="td-person__sub">
                            {roleLabel(s.user.teamRole || s.user.role)} · {s.completed} done · {s.outstanding} open{s.overdue > 0 ? ` · ${s.overdue} overdue` : ''}
                          </span>
                        </span>
                        {s.overdue > 0
                          ? <StatusPill tone="warning">{s.overdue} overdue</StatusPill>
                          : <StatusPill tone={s.outstanding > 0 ? 'pending' : 'success'}>{s.outstanding > 0 ? `${s.outstanding} open` : 'All clear'}</StatusPill>}
                        <span className="td-person__bar"><ProgressBar label="Completed" value={pct} max={100} /></span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </DashboardCard>
        )}

        <DashboardCard title="Needs attention" tint="pastel-coral" meta={<StatusPill tone={overdueList.length ? 'warning' : 'success'}>{overdueList.length} overdue</StatusPill>}
          actions={overdueList.length > 0 ? <Button variant="tertiary" onClick={() => onFilter('overdue')}>See all overdue tasks</Button> : null}>
          {overdueList.length === 0 ? (
            <p className="td-muted">Nothing is overdue.</p>
          ) : (
            <ul className="td-attention">
              {overdueList.slice(0, 6).map(t => (
                <li key={t.id}>
                  <button type="button" className="td-attention__row" onClick={() => onOpenTask(t, overdueList)}>
                    <span className="td-attention__title">{t.title}</span>
                    <StatusPill tone="warning">{dueText(t) || 'Overdue'}</StatusPill>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DashboardCard>

        {!ownOnly && <MovingMostCard tasks={tasks} onOpenTask={onOpenTask} />}
      </div>

      <MemberDetail detail={selectedMember} onClose={() => setSelectedMember(null)}
        onViewTasks={(id) => { setSelectedMember(null); onViewTasks(id); }} />
    </div>
  );
}

// ─── Task Board Tab ────────────────────────────────────────────────────────────
// The filters live in TeamDashboard, so a KPI tile or "View tasks" can set them.
const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'outstanding', label: 'Outstanding' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'completed', label: 'Completed' },
];
const VIEWS = [
  { value: 'board', label: 'Board' },
  { value: 'list', label: 'List' },
  { value: 'byproject', label: 'By project' },
];

// Carla's item 4 (2026-10-12): every list here is searched and paged ON THE SERVER, 15 at a time
// (Team/useTaskPage), so a count is the server's. The board asks once per column; a status filter
// that leaves a column out leaves it empty without asking.
const COLUMN_FOR = {
  all: ['todo', 'overdue', 'done'],
  outstanding: ['todo', 'overdue'],
  overdue: ['overdue'],
  completed: ['done'],
};

function TaskBoardTab({ users, tz, filters, setFilters, canEdit, onOpen, onMove, onDrop, onReassign, isHidden, reloadKey }) {
  const { memberFilter, statusFilter, boardView } = filters;
  const projects = useMemo(() => {
    try { return projectManager.getAllProjects() || []; } catch { return []; }
  }, []);

  // The search box: sent to the server a moment after typing stops.
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  useEffect(() => {
    const id = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(id);
  }, [text]);

  const statusOf = useCallback((t) => taskStatus(t, tz), [tz]);
  const peopleOf = useCallback((t) => [...taskPeopleIds(t)].map(id => users.find(u => u.id === id)).filter(Boolean), [users]);

  const board = boardView === 'board';
  const shown = COLUMN_FOR[statusFilter] || COLUMN_FOR.all;
  const common = { member: memberFilter, q, reloadKey };
  const todo = useTaskPage({ ...common, status: 'todo', enabled: board && shown.includes('todo') });
  const overdue = useTaskPage({ ...common, status: 'overdue', enabled: board && shown.includes('overdue') });
  const done = useTaskPage({ ...common, status: 'done', enabled: board && shown.includes('done') });
  const list = useTaskPage({ ...common, status: statusFilter, enabled: !board });

  const visible = (p) => p.tasks.filter((t) => !isHidden(t.id));
  const columns = [['todo', todo], ['overdue', overdue], ['done', done]].map(([key, p]) => ({
    key, tasks: visible(p), total: p.total, page: p.page, pages: p.pages, onPage: p.setPage,
    loading: p.loading, error: p.error, onRetry: p.retry,
  }));
  const listTasks = visible(list);

  const groupsOf = (rows) => {
    const groups = {};
    rows.forEach(task => {
      const key = task.projectId ? String(task.projectId) : "none";
      if (!groups[key]) groups[key] = [];
      groups[key].push(task);
    });
    return Object.entries(groups)
      .map(([key, groupTasks]) => {
        const project = key !== "none" ? projects.find(p => String(p.id) === key) : null;
        return { key, name: project?.name || (key !== "none" ? `Project ${key}` : "No project"), tasks: groupTasks };
      })
      .sort((a, b) => a.name === "No project" ? 1 : b.name === "No project" ? -1 : a.name.localeCompare(b.name));
  };

  const filtered = memberFilter || statusFilter !== 'all' || text;
  const clear = filtered
    ? <Button variant="tertiary" onClick={() => { setText(''); setQ(''); setFilters({ memberFilter: '', statusFilter: 'all' }); }}>Clear the filters</Button>
    : null;
  const viewProps = { statusOf, peopleOf, canEdit, onOpen, onReassign };

  let listBody = null;
  if (!board) {
    if (list.error) listBody = <ErrorState title="These tasks did not load" message={list.error} onRetry={list.retry} />;
    else if (list.loading && listTasks.length === 0) listBody = <Skeleton lines={4} label="Loading tasks" />;
    else if (boardView === 'list') listBody = <ListView tasks={listTasks} {...viewProps} emptyAction={clear} />;
    else listBody = <ProjectGroups groups={groupsOf(listTasks)} {...viewProps} emptyAction={clear} partial={list.pages > 1} />;
  }

  return (
    <div className="td-stack">
      <div className="td-toolbar">
        <span className="td-toolbar__search">
          <SearchInput label="Search tasks" placeholder="Search by task, project or person" value={text} onChange={e => setText(e.target.value)} />
        </span>
        <span className="td-toolbar__member">
          <Select aria-label="Member" value={memberFilter} onChange={e => setFilters({ memberFilter: e.target.value })}
            placeholder="All members" options={users.map(u => ({ value: u.id, label: u.name }))} />
        </span>
        <ChipGroup label="Status" options={STATUS_FILTERS} value={statusFilter} onChange={v => setFilters({ statusFilter: v })} />
        <span className="td-toolbar__end">
          <SegmentedControl label="View" options={VIEWS} value={boardView} onChange={v => setFilters({ boardView: v })} />
        </span>
      </div>

      {board && <BoardView columns={columns} {...viewProps} onMove={onMove} onDrop={onDrop} />}
      {!board && (
        <div className="td-stack">
          {listBody}
          {!list.error && list.total > 0 && (
            <div className="td-pager-row">
              <span className="td-pager__count">{plural(list.total, 'task')}</span>
              <Pager label="Tasks" page={list.page} pages={list.pages} onPage={list.setPage} disabled={list.loading} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Team Chat Tab ────────────────────────────────────────────────────────────
//
// IT DELIVERED NOTHING (Carla, 2026-10-04, the worst finding of the sprint). Every
// message typed here went to localStorage under `dashboard_team_chat_<workspaceId>`
// and nowhere else. Her words: "My customer opens Team Chat, types a message to a
// colleague, sees it appear, and the colleague never receives it. Both of them draw
// a reasonable and opposite conclusion from the same silence: one believes they
// communicated, the other believes nobody spoke." The proposal-accepted defect,
// between two people in the same business, in the one feature whose entire purpose is
// that a message reaches someone. A real server-backed chat existed in the same
// product the whole time, on the Dashboard.
//
// ── WHY THIS IS NOT A ONE-LINE SWAP, AND THE SHAPE WORTH REMEMBERING ─────────
// This tab offered PER-COLLEAGUE tabs as well as an "All Team" one, and
// team_chat_messages has no recipient column — just user_id, team_id, sender_name,
// body. Pointing the per-person tabs at /api/team-chat would have taken a message
// written privately to one colleague and DELIVERED IT TO THE WHOLE TEAM.
//
// Carla: "fixing a message that reaches nobody by delivering it to everybody is
// worse than the bug… when a store cannot represent a field the UI collects, wiring
// the UI to the store does not fix the gap, it changes who is harmed."
//
// So (her option A): the team room is real, and the per-person tabs are retired as an
// honest "not yet" rather than left pretending. Direct messages need a recipient
// column and a migration, which is a separate decision she has not taken.
//
// ── THE MESSAGES ALREADY STRANDED IN BROWSERS ───────────────────────────────
// They exist only on the device that typed them — there is no server record, which
// IS the bug — so they can only be recovered by this code, on that browser, once.
// Her instruction: "show them, never discard. Nobody has their words deleted on top
// of never having been sent."
//   "all" tab messages      migrated into the team room, and the sender told
//   per-person messages     CANNOT be migrated (that is the broadcast above), so they
//                           are shown back to the sender with a plain sentence, and
//                           she chooses: resend to the team, copy, or dismiss
// Anyone who cleared their storage or changed device has already lost them, and
// nothing can bring those back.
function teamChatKey() {
  return `dashboard_team_chat_${getActiveWorkspaceId() || 'none'}`;
}

// Read the stranded store once, and split it by what can be delivered.
function readStrandedMessages() {
  try {
    const raw = JSON.parse(localStorage.getItem(teamChatKey()) || '{}');
    if (!raw || typeof raw !== 'object') return { team: [], direct: [] };
    const team = Array.isArray(raw.all) ? raw.all : [];
    const direct = Object.entries(raw)
      .filter(([k]) => k !== 'all')
      .flatMap(([k, list]) => (Array.isArray(list) ? list.map(m => ({ ...m, toUserId: k })) : []));
    return { team, direct };
  } catch { return { team: [], direct: [] }; }
}

function TeamChatTab({ currentUser, users }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [stranded, setStranded] = useState(() => readStrandedMessages());
  const [migrateNote, setMigrateNote] = useState('');
  const [noTeam, setNoTeam] = useState(false);
  const [noTeamReason, setNoTeamReason] = useState('');
  const bottomRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await api('/api/team-chat');
      const data = await res.json();
      if (Array.isArray(data.messages)) {
        setMessages(data.messages);
        setLoadFailed(false);
        // NO TEAM IS A THIRD STATE (2026-10-04). The census found 14 of 16 businesses have
        // no legacy_team_id, so for almost everybody this chat could never work at all —
        // and it rendered as an empty room, which reads as "nobody has said anything".
        // The server now says which, and so does the screen.
        setNoTeam(!!data.noTeam);
        setNoTeamReason(data.reason || '');
      } else {
        setLoadFailed(true);   // a 200 with no array is a FAILURE, not an empty room
      }
    } catch {
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // The room is shared, so it changes without this tab doing anything. Polling
  // matches the Dashboard chat's behaviour.
  useEffect(() => {
    const iv = setInterval(() => { load(); }, 15000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  async function post(body) {
    const res = await api('/api/team-chat', { method: 'POST', body: JSON.stringify({ body }) });
    const data = await res.json();
    if (!data?.message) throw Object.assign(new Error('no message returned'), { status: res.status });
    return data.message;
  }

  const handleSend = useCallback(async () => {
    const body = newMessage.trim();
    if (!body || sending) return;
    setSending(true);
    setSendError('');
    try {
      await post(body);
      setNewMessage('');
      await load();   // read the room back rather than assuming what we added
    } catch (err) {
      // It must never again look sent when it was not.
      setSendError(describeWriteFailure(err, 'send that message'));
    } finally {
      setSending(false);
    }
  }, [newMessage, sending, load]);

  // One-time migration of the "all" messages. Only these: a per-person message has a
  // recipient this store cannot hold.
  async function migrateTeamMessages() {
    setMigrateNote('');
    const toSend = stranded.team;
    if (!toSend.length) return;
    let sent = 0;
    try {
      for (const m of toSend) {
        const text = (m.text || '').trim();
        if (!text) continue;
        // Marked, because they are arriving late and out of order relative to the
        // room — pretending otherwise would misrepresent the conversation.
        await post(`[sent earlier, not delivered at the time] ${text}`);
        sent++;
      }
    } catch (err) {
      setMigrateNote(`${sent} of ${toSend.length} were sent before this failed: ${describeWriteFailure(err, 'send them')} The rest are still here.`);
      await load();
      return;
    }
    // Only now is it safe to stop showing them, and only the team half is cleared.
    try {
      const raw = JSON.parse(localStorage.getItem(teamChatKey()) || '{}');
      delete raw.all;
      localStorage.setItem(teamChatKey(), JSON.stringify(raw));
    } catch { /* private mode: they stay listed, which is the safe direction */ }
    setStranded(s => ({ ...s, team: [] }));
    setMigrateNote(`${sent} message${sent === 1 ? '' : 's'} sent to the team room. Your colleagues can see ${sent === 1 ? 'it' : 'them'} now.`);
    await load();
  }

  function dismissDirect() {
    try {
      const raw = JSON.parse(localStorage.getItem(teamChatKey()) || '{}');
      for (const k of Object.keys(raw)) if (k !== 'all') delete raw[k];
      localStorage.setItem(teamChatKey(), JSON.stringify(raw));
    } catch {}
    setStranded(s => ({ ...s, direct: [] }));
  }

  const formatMessageTime = (ts) => formatTaskClock(ts);   // fix(task-dates): the shared formatter
  const nameFor = (id) => users.find(u => u.id === id)?.name || 'a colleague';
  const teamUsers = users.filter(u => u.id !== currentUser?.id);

  return (
    <div className="td-chat">
      {/* ── Messages that never left this browser ─────────────────────────── */}
      {stranded.team.length > 0 && (
        <div data-testid="stranded-team-messages">
          <Banner tone="notice" action={
            <Button variant="secondary" size="sm" onClick={migrateTeamMessages} data-testid="migrate-team-messages">
              Send {stranded.team.length === 1 ? 'it' : 'them'} to the team now
            </Button>
          }>
            <strong>{stranded.team.length} message{stranded.team.length === 1 ? '' : 's'} you sent to the team were never delivered. </strong>
            Team Chat used to save messages only on this device, so these never reached
            anyone. They are still here, and you can send them to the team room now.
            {stranded.team.map((m, i) => (
              <span key={i} className="td-quote" style={{ display: 'block' }}>“{m.text}”</span>
            ))}
          </Banner>
        </div>
      )}

      {stranded.direct.length > 0 && (
        <div data-testid="stranded-direct-messages">
          <Banner tone="notice" action={
            <Button variant="tertiary" size="sm" onClick={dismissDirect} data-testid="dismiss-direct-messages">
              I have these, hide this
            </Button>
          }>
            <strong>{stranded.direct.length} private message{stranded.direct.length === 1 ? '' : 's'} were never delivered. </strong>
            These were written to one person, and they were never sent. Aidey does not
            have one-to-one messages yet, so they cannot be delivered privately, here
            is what you wrote, so you can send it another way or paste it into the team
            room yourself.
            {stranded.direct.map((m, i) => (
              <span key={i} className="td-quote" style={{ display: 'block' }}>
                to {nameFor(m.toUserId)}: “{m.text}”
              </span>
            ))}
          </Banner>
        </div>
      )}

      {migrateNote && (
        <p data-testid="migrate-note" className="td-tip" role="status">
          {migrateNote}
        </p>
      )}

      {/* ── One room, honestly labelled ───────────────────────────────────── */}
      <div className="td-row-wrap">
        <StatusPill tone="neutral"># All team</StatusPill>
        {teamUsers.length > 0 && (
          <span data-testid="dm-not-yet" className="td-muted">
            Everyone on your team sees these messages. One-to-one messages are not
            available yet.
          </span>
        )}
      </div>

      {teamUsers.length === 0 ? (
        <div className="ui-empty">
          <p className="ui-empty__title">No team members yet</p>
          <p className="ui-empty__text">Add more people to start team conversations.</p>
        </div>
      ) : (
        <>
          <div className="td-chat__room">
            {loading ? (
              <Skeleton lines={3} label="Loading the conversation" />
            ) : loadFailed ? (
              <div data-testid="team-chat-load-failed" className="td-chat__state" role="alert">
                We could not load the conversation, so this may not be empty. Refresh to
                try again, and please do not assume nobody has written to you.
              </div>
            ) : noTeam ? (
              /* NO TEAM, CHECKED BEFORE "no messages" — because an empty room and a room that
                 cannot exist are different things, and the second one is not fixed by typing.
                 14 of 16 businesses were in this state and saw the cheerful empty state below,
                 which invites a message that would have been stored where no reader can look. */
              <div data-testid="team-chat-no-team" className="td-chat__state">
                {noTeamReason || 'Team chat needs a team. Invite someone in Settings → Team and you can both use it.'}
              </div>
            ) : messages.length === 0 ? (
              <div className="td-chat__state">
                No messages yet. Say something to the whole team!
              </div>
            ) : (
              messages.map((msg, idx) => (
                <div key={msg.id || idx} className="td-chat__msg">
                  <div className="td-chat__who">
                    <strong>{msg.sender_name}</strong>
                    {msg.created_at && (
                      <span className="td-chat__time">{formatMessageTime(msg.created_at)}</span>
                    )}
                  </div>
                  <p className="td-chat__body">{msg.body}</p>
                </div>
              ))
            )}
            <div ref={bottomRef} />
          </div>

          {sendError && (
            <p data-testid="team-chat-send-failed" className="td-failed" role="alert">{sendError}</p>
          )}

          <div className="td-chat__composer">
            <Input
              type="text"
              value={newMessage}
              onChange={e => setNewMessage(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
              /* THE COMPOSER DOES NOT INVITE WHAT CANNOT BE SENT. With no team the server
                 refuses, so an enabled box asking for a message is the same lie one step
                 earlier — and it is what let people type into a room nobody could read.
                 Disabled with a reason, the honest version of the disabled logo upload. */
              disabled={noTeam}
              placeholder={noTeam ? 'Invite someone to your team first' : 'Send a message to the whole team...'}
              aria-label="Team message"
            />
            <Button
              variant="confirm"
              onClick={handleSend}
              disabled={noTeam || sending || !newMessage.trim()}
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Main TeamDashboard ────────────────────────────────────────────────────────
const TAB_DEFS = [
  { id: 'overview', label: 'Overview', color: 'pastel-periwinkle' },
  { id: 'tasks', label: 'Task board', color: 'pastel-yellow' },
  { id: 'projects', label: 'Projects', color: 'pastel-mint' },
  { id: 'chat', label: 'Team chat', color: 'pastel-blue' },
  { id: 'manage', label: 'Manage team', color: 'pastel-lilac' },
];

function TeamDashboard({ onAddTask }) {
  const { currentUser, isTeamAdmin } = useCurrentUser();
  const tz = currentUser?.timezone;
  const [activeTab, setActiveTab] = useState("overview");
  const [tasks,     setTasks]     = useState([]);
  // Block I: which tasks the server sent ('team' | 'own', routes/tasks.js). Anything but an explicit
  // 'team' is treated as the caller's own, so the page can never call one person's list the team's.
  const [scope,     setScope]     = useState('own');
  const [summary,   setSummary]   = useState(null);   // fresh-eyes 12: the server's team numbers
  const [users,     setUsers]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [loadError, setLoadError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [filters, setFilterState] = useState({ memberFilter: '', statusFilter: 'all', boardView: 'board' });
  const setFilters = (patch) => setFilterState((f) => ({ ...f, ...patch }));
  const [openTaskId, setOpenTaskId] = useState(null);
  // The task as it was when opened (a paged list's row may not be in the full list yet), and, when it
  // was opened from Needs attention, that list's ids for Previous and Next (Carla's item 5).
  const [openSnapshot, setOpenSnapshot] = useState(null);
  const [attentionIds, setAttentionIds] = useState(null);
  // Asks the paged task lists for their page again after a change (Carla's item 4).
  const [reloadKey, setReloadKey] = useState(0);
  const [moving, setMoving] = useState(null);
  const [reassigning, setReassigning] = useState(null);

  const applyTasks = (data) => {
    setTasks(Array.isArray(data?.tasks) ? data.tasks : []);
    setScope(data?.scope === 'team' ? 'team' : 'own');
    setSummary(data?.summary || null);
  };

  const loadTeamData = () => {
    return Promise.all([
      api('/api/team/members'),
      api('/api/tasks?team=true'),
    ]);
  };

  // api() THROWS on a non-2xx (src/api/client.js): the `.ok` checks after it were dead code.
  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const [membersRes, tasksRes] = await loadTeamData();
      const membersData = await membersRes.json();
      const tasksData = await tasksRes.json();
      setUsers((membersData.members || []).filter(u => u.status !== "inactive"));
      applyTasks(tasksData);
    } catch (err) {
      setLoadError(messageFromError(err, "Your team's tasks could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Refresh tasks when switching to Task Board. A failure is said (it was swallowed, and the board
  // then showed the older list as if current).
  const refreshTasks = useCallback(async () => {
    try {
      const res = await api('/api/tasks?team=true');
      applyTasks(await res.json());
      setRefreshError('');
    } catch (err) {
      setRefreshError(messageFromError(err, 'The task board could not be refreshed.'));
    }
  }, []);
  useEffect(() => {
    if (activeTab === "tasks") refreshTasks();
  }, [activeTab, refreshTasks]);

  // Decision 7: a delete waits behind Undo, through the existing DELETE /api/tasks/:id.
  const taskDelete = useDelayedDelete({
    send: (t, { keepalive }) => api(`/api/tasks/${t.id}`, { method: 'DELETE', keepalive }),
    onDeleted: (t) => { setTasks((list) => list.filter((x) => x.id !== t.id)); setReloadKey((k) => k + 1); refreshTasks(); },
    describe: (t) => `"${t.title}" deleted.`,
    failMessage: 'That task could not be deleted.',
  });
  const visibleTasks = useMemo(() => tasks.filter(t => !taskDelete.isHidden(t.id)), [tasks, taskDelete]);

  const ownOnly = scope !== 'team';
  const stats = useMemo(() => memberStatsFrom(summary, users), [summary, users]);

  // Who may change a task is the server's rule (lib/taskPermissions; Carla's item 1, 2026-10-11): the
  // creator, or the owner or an admin for a task in the business. The team list says so per task
  // (canEdit); a row without the answer (an older server) falls back to "the creator only".
  const canEdit = useCallback((t) => {
    if (!currentUser?.id || t.isBirthday) return false;
    return typeof t.canEdit === 'boolean' ? t.canEdit : t.userId === currentUser.id;
  }, [currentUser]);
  const peopleOf = useCallback((t) => [...taskPeopleIds(t)].map(id => users.find(u => u.id === id)).filter(Boolean), [users]);

  // The existing PUT /api/tasks/:id. The list is updated from the task the server sends back, never
  // from what was asked for; anything else throws for the caller to show.
  async function putTask(task, patch) {
    const res = await api(`/api/tasks/${task.id}`, { method: 'PUT', body: JSON.stringify(patch) });
    const data = await res.json();
    if (!data?.task) throw new Error('The server did not confirm the change. Refresh to check.');
    setTasks(list => list.map(t => (t.id === task.id ? { ...t, ...data.task } : t)));
    setOpenSnapshot(s => (s && s.id === task.id ? { ...s, ...data.task } : s));
    setReloadKey((k) => k + 1);
    refreshTasks();   // the server's numbers again (fresh-eyes 12)
    return data.task;
  }

  // "Move to…": the tap route on the board, and the drop of a drag (Block M2, UI-16b): both call
  // moveTask, the one update. Overdue is worked out from the due date, so a card moves between To do
  // and Done; a drop that would need the date changed moves nothing and says why.
  function dropTask(task, toColumn) {
    const { dest, note } = dropDestination(taskStatus(task, tz), toColumn);
    if (dest) moveTask(task, dest, note);
    else if (note) showToast(note, { type: 'info' });
  }
  async function moveTask(task, dest, note) {
    try {
      await putTask(task, { completed: dest === 'done' });
      showToast(dest === 'done' ? `"${task.title}" marked as done.` : `"${task.title}" reopened.${note ? ` ${note.replace(/^Reopened\. /, '')}` : ''}`, { type: 'success' });
    } catch (err) {
      showErrorToast(messageFromError(err, 'That move was not saved.'));
    }
  }

  const openTask = openTaskId == null || taskDelete.isHidden(openTaskId) ? null
    : (visibleTasks.find(t => t.id === openTaskId) || (openSnapshot?.id === openTaskId ? openSnapshot : null));
  // list: the Needs attention list it was opened from, for Previous and Next; null from anywhere else.
  const openFrom = (t, list = null) => {
    setOpenTaskId(t.id);
    setOpenSnapshot(t);
    setAttentionIds(list ? list.map(x => x.id) : null);
  };
  const closeTask = () => { setOpenTaskId(null); setOpenSnapshot(null); setAttentionIds(null); };
  const navAt = attentionIds && openTask ? attentionIds.indexOf(openTask.id) : -1;
  const goAttention = (i) => {
    const id = attentionIds[i];
    const t = visibleTasks.find(x => x.id === id);
    if (t) { setOpenTaskId(id); setOpenSnapshot(t); }
  };
  const nav = navAt >= 0 ? {
    index: navAt, total: attentionIds.length, label: 'Needs attention',
    onPrev: () => goAttention(navAt - 1), onNext: () => goAttention(navAt + 1),
  } : null;
  // The server's numbers (fresh-eyes 12), the same ones the Dashboard tile shows.
  const outstanding = summary ? summary.outstanding : null;
  const overdue = summary ? summary.overdue : null;
  const goFilter = (statusFilter, memberFilter = '') => { setFilters({ statusFilter, memberFilter }); setActiveTab('tasks'); };

  const tabs = TAB_DEFS
    .filter(t => t.id !== 'manage' || isTeamAdmin)
    .map(t => (t.id === 'tasks' && outstanding != null ? { ...t, count: outstanding } : t));

  const subline = outstanding == null ? plural(users.length, 'member')
    : ownOnly
    ? `${plural(users.length, 'member')} · your ${plural(outstanding, 'outstanding task')}${overdue ? ` · ${overdue} overdue` : ''}`
    : `${plural(users.length, 'member')} · ${plural(outstanding, 'outstanding task')} · ${overdue} overdue`;
  const primary = onAddTask && !['projects', 'manage'].includes(activeTab)
    ? <AddTaskButton onClick={onAddTask} />
    : null;

  if (loading) {
    return (
      <PageLayout>
        <PageHeader eyebrow={{ label: 'Team', color: 'group-team' }} title="Team dashboard" />
        <Skeleton lines={5} label="Loading your team" />
      </PageLayout>
    );
  }

  if (loadError) {
    return (
      <PageLayout>
        <PageHeader eyebrow={{ label: 'Team', color: 'group-team' }} title="Team dashboard" />
        <ErrorState title="Your team did not load" message={loadError} onRetry={load} />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader eyebrow={{ label: 'Team', color: 'group-team' }} title="Team dashboard" subline={subline} primary={primary} />
      <div className="td-stack">
        <TabGroup label="Team dashboard" idPrefix="td" tabs={tabs} value={activeTab} onChange={setActiveTab} />

        {ownOnly && ['overview', 'tasks'].includes(activeTab) && (
          <Banner tone="info">
            These are your own tasks. Only the owner and the finance role see the whole team's tasks, so team totals and workload are not shown here.
          </Banner>
        )}

        <TabPanel idPrefix="td" tabId={activeTab}>
          {activeTab === "overview" && (
            <OverviewTab stats={stats} summary={summary} tasks={visibleTasks} tz={tz} ownOnly={ownOnly}
              onFilter={(f) => goFilter(f)} onOpenTask={openFrom}
              onViewTasks={(id) => goFilter('all', id)} />
          )}

          {activeTab === "tasks" && (
            <div className="td-stack">
              {refreshError && (
                <Banner tone="problem" action={<Button size="sm" variant="secondary" onClick={() => { refreshTasks(); setReloadKey((k) => k + 1); }}>Try again</Button>}>
                  {refreshError} The board below may be out of date.
                </Banner>
              )}
              <TaskBoardTab users={users} tz={tz} filters={filters} setFilters={setFilters}
                canEdit={canEdit} onOpen={(t) => openFrom(t)} onMove={setMoving} onDrop={dropTask} onReassign={setReassigning}
                isHidden={taskDelete.isHidden} reloadKey={reloadKey} />
            </div>
          )}

          {activeTab === "projects" && (
            <Suspense fallback={<Skeleton lines={4} label="Loading projects" />}>
              <ProjectTracker embedded />
            </Suspense>
          )}

          {activeTab === "chat" && (
            <TeamChatTab currentUser={currentUser} users={users} />
          )}

          {activeTab === "manage" && (
            <Suspense fallback={<Skeleton lines={4} label="Loading your team" />}>
              <TeamManagementLazy embedded onViewTasks={(id) => goFilter('all', id)} />
            </Suspense>
          )}
        </TabPanel>
      </div>

      <TaskDrawer
        task={openTask}
        status={openTask ? taskStatus(openTask, tz) : 'todo'}
        people={openTask ? peopleOf(openTask) : []}
        owner={openTask ? users.find(u => u.id === openTask.userId) : null}
        editedBy={openTask?.lastEditedBy ? (users.find(u => u.id === openTask.lastEditedBy) || { name: 'a former member' }) : null}
        canEdit={openTask ? canEdit(openTask) : false}
        nav={nav}
        onClose={closeTask}
        onSave={(t, patch) => putTask(t, patch)}
        onToggleDone={(t) => putTask(t, { completed: !t.completed })}
        onDelete={(t) => { closeTask(); taskDelete.remove(t); }}
        onReassign={setReassigning}
      />

      <ReassignSheet task={reassigning} users={users} onClose={() => setReassigning(null)}
        onAssign={(t, ids) => putTask(t, { assignedTeam: ids })} />

      <MoveToSheet
        open={!!moving}
        onClose={() => setMoving(null)}
        itemName={moving?.title}
        current={moving?.completed ? 'done' : 'todo'}
        destinations={[
          { id: 'todo', label: 'To do', hint: moving && !moving.completed && isTaskOverdue(moving, tz) ? 'Past its due date, so it shows under Overdue' : undefined },
          { id: 'done', label: 'Done' },
        ]}
        onMove={(d) => { const t = moving; if (t) moveTask(t, d.id); }}
      />
    </PageLayout>
  );
}

export default TeamDashboard;

// Exported for its test only. TeamChatTab is the piece whose whole contract is "a
// message reaches someone", so it is tested directly rather than through the whole
// dashboard — which would need projects, tasks, rosters and a plan gate to render.
export { TeamChatTab as __TeamChatTab };
